import { Component, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useChargeErrorCopy } from '../lib/useChargeErrorCopy';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui';
import { useLanguage } from '@/contexts/LanguageContext';

import { apiClient, formatCurrency, minorToMajor } from '@/lib';
import { loyaltyApi, reportPaymentClientEvent } from '@/lib/api';
import type { GuestLoyaltyBalance } from '@/types';
import type { Option, PurchaseSelection, SidekickUpsell } from '@besty/shared/sidekickUpsell';
import { calculateTotal } from '@besty/shared/sidekickUpsell';
import { GuestyPayCardModal } from '@/components/GuestyPayCardModal';
import { CcAuthModal } from '@/components/CcAuthModal';
import { PortalOutletContext } from '@/components/portal/PortalShellContainer';

// Upsell ids look like `legacy:custom:<reservationId>:<customId>`. A card is
// credit-eligible iff its parsed <customId> is in creditEligibleUpsellIds.
function parseCustomUpsellId(upsellId: string): number | null {
  const match = /^legacy:custom:\d+:(\d+)$/.exec(upsellId);
  return match ? Number(match[1]) : null;
}

type DisplayOption = Option & { displayName?: string };

interface PurchaseRow {
  id: string;
  sidekickUpsellId: string;
  amountInCents: number;
  currency: string;
  paymentMethod: string;
  purchaseDetails:
    | { type: 'option'; optionName: string; optionPrice: number }
    | { type: 'quantity'; quantities: Record<string, number> };
  createdAt: string;
}

// BES-7535: mirrors the backend portal row union (legacyTransform.ts). Present
// only on grayed-out cards; absent means the card is interactive.
type UpsellUnavailableReason =
  | 'missed_window'
  | 'dates_unavailable'
  | 'not_offered_for_stay'
  | 'awaiting_payment'
  | 'payment_failed'
  | 'purchased'
  | 'unavailable';

// BES-7462: params to re-render the Airbnb payment instructions for an
// awaiting_payment card. Present only on awaiting_payment rows (set by the
// backend); absent means the card stays grayed in "No longer available".
interface UpsellInstructions {
  flow: 'resolution_center' | 'reservation_alteration';
  airbnbUrl: string | null;
  priceMinor: number | null;
  currency: string;
  optionName: string | null;
}

interface UpsellWithPurchases {
  id: string;
  reservationId: number;
  userId: number;
  upsellData: SidekickUpsell;
  status: string;
  createdAt: string;
  updatedAt: string;
  purchases: PurchaseRow[];
  unavailableReason?: UpsellUnavailableReason;
  instructions?: UpsellInstructions;
}

interface UpsellsResponse {
  upsells: UpsellWithPurchases[];
  isAirbnb?: boolean;
  airbnbOffPlatformUpsellsEnabled?: boolean;
  ccAuthRequired?: boolean;
}

interface PurchaseResponse {
  checkoutUrl?: string;
  purchased?: boolean;
  purchaseId?: string;
  requiresCardInput?: boolean;
  paymentProviderId?: string | null;
  listingId?: string | null;
  pmsReservationId?: string | null;
  accountId?: string | null;
}

interface ConfirmAirbnbResponse {
  confirmed: boolean;
  flow: 'resolution_center' | 'reservation_alteration';
  alterationNotice24h?: boolean;
  airbnbUrl: string | null;
  title: string;
  price: number;
  currency: string;
}

function QuantityStepper({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-11 h-11 rounded-full border border-zinc-300 flex items-center justify-center text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-100 active:scale-95 transition-all"
      >
        <svg width="16" height="2" viewBox="0 0 16 2" fill="none">
          <path d="M1 1h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      <span className="w-8 text-center text-lg font-semibold tabular-nums text-zinc-900">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-11 h-11 rounded-full border border-zinc-300 flex items-center justify-center text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-100 active:scale-95 transition-all"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

function UpsellCard({
  upsell,
  onPurchase,
  submitting,
  isAirbnb,
  offPlatformEnabled,
  loyaltyBalance,
}: {
  upsell: UpsellWithPurchases;
  onPurchase: (upsellId: string, selection: PurchaseSelection, appliedLoyaltyCredit: number) => void;
  submitting: boolean;
  isAirbnb: boolean;
  offPlatformEnabled: boolean;
  loyaltyBalance: GuestLoyaltyBalance | null;
}) {
  const { t } = useTranslation('upsells');
  const data = upsell.upsellData;

  // BES-7708: backend sends 'Estimated tax' for GuestyPay hosts, else 'Tax'.
  // Translate the known literals directly so i18next-parser extracts them; pass
  // any other backend-provided name through unchanged.
  const translateTaxName = (name: string): string => {
    switch (name) {
      case 'Estimated tax':
        return t('Estimated tax');
      case 'Tax':
        return t('Tax');
      default:
        return name;
    }
  };

  // Loyalty stored-credit eligibility: active member with positive wallet and a
  // custom upsell id that the program allows credit to be spent on.
  const customId = parseCustomUpsellId(upsell.id);
  const isCreditEligible =
    !!loyaltyBalance &&
    loyaltyBalance.membershipId !== null &&
    loyaltyBalance.storedCreditCents > 0 &&
    customId !== null &&
    loyaltyBalance.creditEligibleUpsellIds.includes(customId);
  const [applyCredit, setApplyCredit] = useState(true);
  // Off-platform Airbnb non-BB add-ons are charged via Stripe/GuestyPay (normal
  // purchase flow); BB and non-off-platform Airbnb keep the Airbnb confirmation flow.
  const usesAirbnbConfirm = isAirbnb && (!offPlatformEnabled || upsell.id.startsWith('legacy:bb_'));
  const isOptionMode = data.mode === 'option';

  // BES-7087: pre-reserve visual distinction (muted button + badge +
  // subtitle). `availableAt` is the real moment the offer flips to a
  // bookable reserve (host's eciLcoSettings.lower_bound), shipped by
  // the backend; fall back to a generic label when absent. NOTE:
  // `expiresAt` is the Stripe-hold expiry, NOT the opens-at time —
  // only its `event` field is safe to read for copy.
  const isHold = data.isHold;
  const holdAvailabilityLabel = (() => {
    if (!data.isHold) return null;
    if (data.availableAt) {
      const opensAt = new Date(data.availableAt);
      if (!Number.isNaN(opensAt.getTime())) {
        const date = opensAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        return t('Becomes available {{date}}', { date });
      }
    }
    return data.expiresAt.event === 'checkin'
      ? t('Confirmed closer to your check-in')
      : t('Confirmed closer to your check-out');
  })();
  // Option mode state
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  // Quantity mode state
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    if (data.mode !== 'quantity') return {};
    const init: Record<string, number> = {};
    for (const v of data.quantityPricing.variables) {
      init[v.variableName] = v.min;
    }
    return init;
  });

  // Check if option mode is already purchased
  const isOptionPurchased = isOptionMode && upsell.purchases.length > 0;

  // For quantity mode, compute cumulative purchased quantities
  const cumulativeQuantities: Record<string, number> = {};
  if (data.mode === 'quantity') {
    for (const purchase of upsell.purchases) {
      if (purchase.purchaseDetails.type === 'quantity') {
        for (const [k, v] of Object.entries(purchase.purchaseDetails.quantities)) {
          cumulativeQuantities[k] = (cumulativeQuantities[k] || 0) + v;
        }
      }
    }
  }

  // Build selection for price calc
  const selection: PurchaseSelection | null = (() => {
    if (data.mode === 'option' && selectedOption) {
      const opt = data.options.find((o) => o.name === selectedOption);
      if (opt) return { type: 'option' as const, optionName: opt.name, optionPrice: opt.price };
    }
    if (data.mode === 'quantity') {
      const filtered: Record<string, number> = {};
      for (const v of data.quantityPricing.variables) {
        const effectiveMax = v.max - (cumulativeQuantities[v.variableName] || 0);
        if (effectiveMax >= v.min) {
          filtered[v.variableName] = quantities[v.variableName] ?? v.min;
        } else {
          filtered[v.variableName] = 0; // sold-out vars must stay in record for expression evaluator
        }
      }
      const hasAny = Object.values(filtered).some((v) => v > 0);
      if (hasAny) return { type: 'quantity' as const, quantities: filtered };
    }
    return null;
  })();

  // Calculate price breakdown
  const breakdown = selection ? calculateTotal(data, selection) : null;

  // Display-only estimate; backend re-caps at the live balance + charge.
  const showCredit = isCreditEligible && applyCredit && !!breakdown && breakdown.total > 0;
  const appliedCents =
    isCreditEligible && applyCredit && breakdown && breakdown.total > 0 && loyaltyBalance
      ? Math.min(loyaltyBalance.storedCreditCents, breakdown.total)
      : 0;
  const totalAfterCredit = breakdown ? breakdown.total - appliedCents : 0;

  const handleSubmit = () => {
    if (!selection) return;
    onPurchase(upsell.id, selection, appliedCents);
  };

  const getOptionDisplayName = (option: DisplayOption) => option.displayName || option.name;

  return (
    <div
      className="bg-white rounded-2xl border border-zinc-200 overflow-hidden"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}
    >
      {/* Image gallery (horizontally scrollable; internal S3 URLs) */}
      {data.imageUrls && data.imageUrls.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-5 pt-5 pb-1 snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {data.imageUrls.map((url, i) => (
            <img
              key={`${url}-${i}`}
              src={url}
              alt=""
              loading="lazy"
              className="aspect-[16/9] w-64 flex-none snap-start rounded-xl object-cover bg-zinc-100"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          ))}
        </div>
      )}

      {/* Header */}
      <div className="pt-6 pb-4 px-5">
        <h3 className="text-xl font-bold tracking-tight text-zinc-900">{data.title}</h3>
        {data.description && <p className="mt-1.5 text-[15px] leading-relaxed text-zinc-500">{data.description}</p>}
      </div>

      <div className="border-t border-zinc-100" />

      <div className="px-5 pb-5 pt-4 space-y-5">
        {/* Option mode */}
        {isOptionMode && data.mode === 'option' && (
          <div className="space-y-3">
            {data.options.map((opt: DisplayOption) => {
              const purchasedThis = upsell.purchases.some(
                (p) => p.purchaseDetails.type === 'option' && p.purchaseDetails.optionName === opt.name
              );
              const isSelected = selectedOption === opt.name;
              // BES-9673: per-option hold state. On the Airbnb confirm path a
              // pre-reserve can't complete (no card to hold), so hold options
              // gray out individually while block_limit-pinned 'reserve' slots
              // stay bookable (BES-7087 used to gray the whole card). Options
              // without the per-option flag inherit the card-level isHold. On
              // Stripe-capable paths (direct + Airbnb off-platform) the whole
              // card keeps the card-level pre-reserve treatment: rendering is
              // unchanged there, and purchase still holds by card-level isHold.
              const optHold = usesAirbnbConfirm ? (opt.isHold ?? isHold) : isHold;
              const optBlocked = optHold && usesAirbnbConfirm;
              return (
                <button
                  key={opt.name}
                  type="button"
                  onClick={() => !isOptionPurchased && !optBlocked && setSelectedOption(opt.name)}
                  disabled={isOptionPurchased || optBlocked}
                  className={[
                    'w-full flex items-start gap-3 rounded-2xl border p-4 text-left transition-all',
                    purchasedThis
                      ? 'border-green-300 bg-green-50/60'
                      : isSelected
                        ? 'border-[var(--besty-blue,#3b82f6)] bg-blue-50/60 ring-2 ring-blue-200/60'
                        : optBlocked
                          ? 'border-zinc-200 bg-zinc-50'
                          : optHold
                            ? 'border-amber-200 bg-amber-50/40 hover:border-amber-300'
                            : 'border-zinc-200 hover:border-zinc-300',
                    isOptionPurchased && !purchasedThis ? 'opacity-50 cursor-not-allowed' : '',
                    optBlocked ? 'opacity-60 cursor-not-allowed' : '',
                  ].join(' ')}
                >
                  {/* Radio dot (hidden for blocked holds — there's nothing to select) */}
                  {!optBlocked && (
                    <span
                      aria-hidden
                      className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border transition-colors"
                      style={{
                        backgroundColor: purchasedThis ? '#16a34a' : isSelected ? 'var(--besty-blue, #3b82f6)' : 'white',
                        borderColor: purchasedThis ? 'transparent' : isSelected ? 'transparent' : '#d1d5db',
                      }}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full bg-white transition-opacity ${
                          isSelected || purchasedThis ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    </span>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-semibold ${optHold ? 'text-zinc-600' : 'text-zinc-900'}`}>
                        {getOptionDisplayName(opt)}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`font-semibold ${optHold ? 'text-zinc-600' : 'text-zinc-900'}`}>
                          {formatCurrency(opt.price, data.currency)}
                        </span>
                        {purchasedThis && (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                            {t('Purchased')}
                          </span>
                        )}
                        {optBlocked && !purchasedThis && (
                          <span className="inline-flex items-center rounded-full bg-zinc-200 px-2.5 py-0.5 text-xs font-medium text-zinc-600">
                            {t('Not yet available')}
                          </span>
                        )}
                        {optHold && !optBlocked && !purchasedThis && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                            {t('Pre-reserve')}
                          </span>
                        )}
                      </div>
                    </div>
                    {opt.actionModifier && (
                      <span className="block mt-0.5 text-sm text-zinc-500">{opt.actionModifier}</span>
                    )}
                    {optHold && holdAvailabilityLabel && (
                      <span
                        className={`block mt-0.5 text-sm ${optBlocked ? 'text-zinc-500' : 'text-amber-700'}`}
                      >
                        {holdAvailabilityLabel}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Quantity mode */}
        {data.mode === 'quantity' && (
          <div className="space-y-4">
            {data.quantityPricing.variables.map((v) => {
              const alreadyBought = cumulativeQuantities[v.variableName] || 0;
              const effectiveMax = v.max - alreadyBought;
              if (effectiveMax < v.min) return null;
              return (
                <div key={v.variableName} className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-zinc-900">{v.label}</span>
                    {alreadyBought > 0 && (
                      <span className="block mt-0.5 text-xs text-zinc-500">
                        ({t('{{count}} already purchased', { count: alreadyBought })})
                      </span>
                    )}
                  </div>
                  <QuantityStepper
                    value={quantities[v.variableName] ?? v.min}
                    min={v.min}
                    max={effectiveMax}
                    onChange={(val) => setQuantities((prev) => ({ ...prev, [v.variableName]: val }))}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Price breakdown */}
        {breakdown && (
          <div className="bg-zinc-50/80 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between text-zinc-600">
              <span>{t('Subtotal')}</span>
              <span>{formatCurrency(minorToMajor(breakdown.subtotal, data.currency), data.currency)}</span>
            </div>
            {breakdown.taxLines.map((tax, i) => (
              <div key={i} className="flex justify-between text-zinc-500">
                <span>{translateTaxName(tax.name)}</span>
                <span>{formatCurrency(minorToMajor(tax.amount, data.currency), data.currency)}</span>
              </div>
            ))}
            {breakdown.feeLines.map((fee, i) => (
              <div key={i} className="flex justify-between text-zinc-500">
                <span>{fee.name}</span>
                <span>{formatCurrency(minorToMajor(fee.amount, data.currency), data.currency)}</span>
              </div>
            ))}
            {showCredit && (
              <div className="flex justify-between text-zinc-600">
                <span>{t('Loyalty credit')}</span>
                <span>-{formatCurrency(minorToMajor(appliedCents, data.currency), data.currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-semibold text-zinc-900 pt-2 border-t border-zinc-200">
              <span>{t('Total')}</span>
              <span>
                {formatCurrency(
                  minorToMajor(showCredit ? totalAfterCredit : breakdown.total, data.currency),
                  data.currency
                )}
              </span>
            </div>
          </div>
        )}

        {/* Loyalty stored-credit controls */}
        {isCreditEligible && breakdown && breakdown.total > 0 && loyaltyBalance && (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-4 space-y-2">
            <p className="text-sm text-zinc-600">
              {t('You have {{amount}} in loyalty credit', {
                amount: formatCurrency(
                  minorToMajor(loyaltyBalance.storedCreditCents, data.currency),
                  data.currency
                ),
              })}
            </p>
            <label className="flex items-center gap-2.5 text-sm font-medium text-zinc-900 cursor-pointer">
              <input
                type="checkbox"
                checked={applyCredit}
                onChange={(e) => setApplyCredit(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-[var(--besty-blue,#3b82f6)] focus:ring-blue-200"
              />
              {t('Apply loyalty credit')}
            </label>
          </div>
        )}

        {/* Submit */}
        {!isOptionPurchased && selection && breakdown && (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full rounded-full h-14 text-base font-medium"
            variant="primary"
            size="lg"
          >
            {submitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white mr-2" />
                {t('Processing...')}
              </div>
            ) : usesAirbnbConfirm ? (
              t('Confirm Selection')
            ) : (
              t('Purchase for {{amount}}', {
                amount: formatCurrency(
                  minorToMajor(showCredit ? totalAfterCredit : breakdown.total, data.currency),
                  data.currency
                ),
              })
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// BES-7535: isolates the new grayed-out "unavailable" section so a render error
// there can never unmount the purchasable add-ons or white-screen the page. On
// error it renders nothing, degrading the new feature gracefully.
class UnavailableSectionBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown) {
    console.error('Unavailable add-ons section failed to render', error);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

// BES-7535: a once-offerable add-on that isn't purchasable for this reservation.
// Grayed, non-interactive, with a short reason so guests understand why an option
// they expected to see is missing.
function UnavailableUpsellCard({ upsell }: { upsell: UpsellWithPurchases }) {
  const { t } = useTranslation('upsells');
  const data = upsell.upsellData;

  // Defensive: a malformed row must not throw and white-screen the page.
  if (!data) return null;

  const reasonLabel = (() => {
    switch (upsell.unavailableReason) {
      case 'missed_window':
        return t('You missed the window for this add-on');
      case 'dates_unavailable':
        return t('No longer available for your dates');
      case 'not_offered_for_stay':
        return t('Not offered for your stay dates');
      case 'awaiting_payment':
        return t('Awaiting payment confirmation');
      case 'payment_failed':
        return t("Payment didn't go through, please contact your host");
      case 'purchased':
        return t('Purchased');
      default:
        return t('Not available right now');
    }
  })();

  const isPurchased = upsell.unavailableReason === 'purchased';

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden opacity-60">
      <div className="pt-6 pb-5 px-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-xl font-bold tracking-tight text-zinc-700">{data.title}</h3>
          <span
            className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              isPurchased ? 'bg-green-100 text-green-700' : 'bg-zinc-200 text-zinc-600'
            }`}
          >
            {isPurchased ? t('Purchased') : t('Unavailable')}
          </span>
        </div>
        {data.description && <p className="mt-1.5 text-[15px] leading-relaxed text-zinc-500">{data.description}</p>}
        <p className="mt-3 text-sm text-zinc-500">{reasonLabel}</p>
      </div>
    </div>
  );
}

// BES-7462: an add-on the guest confirmed on Airbnb but hasn't paid for yet.
// Unlike the grayed unavailable cards, this is actionable: tapping re-opens the
// Airbnb payment instructions (Resolution Center steps + deep link) the guest
// saw on confirm, so they can finish paying.
function PendingPaymentCard({ upsell, onResume }: { upsell: UpsellWithPurchases; onResume: () => void }) {
  const { t } = useTranslation('upsells');
  const data = upsell.upsellData;

  if (!data) return null;

  return (
    <button
      type="button"
      onClick={onResume}
      className="w-full text-left bg-white rounded-2xl border border-amber-200 overflow-hidden hover:border-amber-300 active:scale-[0.99] transition-all"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}
    >
      <div className="pt-6 pb-5 px-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-xl font-bold tracking-tight text-zinc-900">{data.title}</h3>
          <span className="shrink-0 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            {t('Payment pending')}
          </span>
        </div>
        {data.description && <p className="mt-1.5 text-[15px] leading-relaxed text-zinc-500">{data.description}</p>}
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-amber-700">{t('Tap to finish paying on Airbnb')}</span>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="shrink-0 text-amber-700"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
}

function UpsellGuestyPayCardModal({
  paymentProviderId,
  listingId,
  pmsReservationId,
  upsellId,
  selection,
  amount,
  currency,
  appliedLoyaltyCredit,
  onSuccess,
  onCancel,
}: {
  paymentProviderId: string;
  listingId?: string | null;
  pmsReservationId?: string | null;
  upsellId: string;
  selection: PurchaseSelection;
  amount: number;
  currency: string;
  appliedLoyaltyCredit: number;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const handleTokenized = useCallback(
    async (tokenId: string) => {
      const result = await apiClient.post<PurchaseResponse>('/upsells/purchase-with-token', {
        upsellId,
        selection,
        tokenId,
        paymentProviderId,
        ...(appliedLoyaltyCredit > 0 ? { appliedLoyaltyCredit } : {}),
      });
      if (result.purchased) {
        onSuccess();
      } else {
        throw new Error('Payment failed');
      }
    },
    [upsellId, selection, paymentProviderId, appliedLoyaltyCredit, onSuccess]
  );

  return (
    <GuestyPayCardModal
      paymentProviderId={paymentProviderId}
      listingId={listingId}
      reservationId={pmsReservationId}
      productKey={upsellId}
      amount={amount}
      currency={currency}
      onTokenized={handleTokenized}
      onCancel={onCancel}
      onError={(stage, err) =>
        reportPaymentClientEvent(stage, err, {
          upsellId,
          paymentProviderId,
          listingId,
          pmsReservationId,
          amount,
          currency,
        })
      }
    />
  );
}

function Upsells() {
  const { t } = useTranslation('upsells');
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const portalOutlet = useOutletContext<PortalOutletContext | null>();
  const refetchPortal = portalOutlet?.refetch;
  const inPortal = location.pathname.startsWith('/portal/');
  const resultPath = inPortal ? '/portal/home/add-on-result' : '/add-on-result';
  const instructionsPath = inPortal ? '/portal/home/add-on-instructions' : '/add-on-instructions';
  const [upsells, setUpsells] = useState<UpsellWithPurchases[]>([]);
  const [loyaltyBalance, setLoyaltyBalance] = useState<GuestLoyaltyBalance | null>(null);
  const [isAirbnb, setIsAirbnb] = useState(false);
  const [offPlatformEnabled, setOffPlatformEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chargeErrorCopy = useChargeErrorCopy();
  const [submitting, setSubmitting] = useState(false);
  const [cardModalState, setCardModalState] = useState<{
    upsellId: string;
    selection: PurchaseSelection;
    paymentProviderId: string;
    listingId?: string | null;
    pmsReservationId?: string | null;
    amount: number;
    currency: string;
    appliedLoyaltyCredit: number;
  } | null>(null);
  const [ccAuthRequired, setCcAuthRequired] = useState(false);
  const [ccAuthPending, setCcAuthPending] = useState<{
    authType: 'charge';
    amount: number;
    currency: string;
    context: Record<string, unknown>;
    run: () => Promise<void>;
  } | null>(null);

  useEffect(() => {
    document.title = t('Add-Ons');
  }, [t]);

  // Fetch the member's loyalty stored-credit balance once on mount. Any error or
  // a null membershipId means "not a member": show no credit UI.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const balance = await loyaltyApi.getGuestBalance();
        if (!cancelled) setLoyaltyBalance(balance.membershipId !== null ? balance : null);
      } catch {
        if (!cancelled) setLoyaltyBalance(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<UpsellsResponse>(
          `/upsells${language !== 'en' ? `?language=${language}` : ''}`
        );
        if (!cancelled) {
          setUpsells(response.upsells);
          setIsAirbnb(Boolean(response.isAirbnb));
          setOffPlatformEnabled(Boolean(response.airbnbOffPlatformUpsellsEnabled));
          setCcAuthRequired(Boolean(response.ccAuthRequired));
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('Failed to load add-ons'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [language]);

  // Runs the actual purchase request. Split out so the BES-7449 credit-card
  // authorization step can run before it for gated hosts.
  const proceedUpsellPurchase = async (
    upsellId: string,
    selection: PurchaseSelection,
    appliedLoyaltyCredit: number
  ) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await apiClient.post<PurchaseResponse>('/upsells/purchase', {
        upsellId,
        selection,
        ...(appliedLoyaltyCredit > 0 ? { appliedLoyaltyCredit } : {}),
      });

      if (response.checkoutUrl) {
        window.location.assign(response.checkoutUrl);
        return;
      }

      if (response.requiresCardInput) {
        if (!response.paymentProviderId) {
          setError(t('Unable to process payment at this time. Please contact the host.'));
          return;
        }
        const upsell = upsells.find((u) => u.id === upsellId);
        const breakdown = upsell ? calculateTotal(upsell.upsellData, selection) : null;
        setCardModalState({
          upsellId,
          selection,
          paymentProviderId: response.paymentProviderId,
          listingId: response.listingId,
          pmsReservationId: response.pmsReservationId,
          amount: breakdown ? breakdown.total / 100 : 0,
          currency: upsell?.upsellData.currency ?? 'USD',
          appliedLoyaltyCredit,
        });
        return;
      }

      if (response.purchased) {
        const upsell = upsells.find((u) => u.id === upsellId);
        const breakdown = upsell ? calculateTotal(upsell.upsellData, selection) : null;
        void refetchPortal?.();
        navigate(
          `${resultPath}?status=success&upsellId=${upsellId}&amount=${breakdown?.total ?? 0}&currency=${upsell?.upsellData.currency ?? 'USD'}`
        );
      }
    } catch (err) {
      setError(
          chargeErrorCopy(
            (err as { code?: string } | undefined)?.code,
            err instanceof Error ? err.message : undefined
          )
        );
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchase = async (
    upsellId: string,
    selection: PurchaseSelection,
    appliedLoyaltyCredit: number
  ) => {
    // Off-platform Airbnb non-BB add-ons go through the normal Stripe/GuestyPay
    // purchase flow; BB and non-off-platform Airbnb use the Airbnb confirmation flow.
    const usesAirbnbConfirm = isAirbnb && (!offPlatformEnabled || upsellId.startsWith('legacy:bb_'));
    if (usesAirbnbConfirm) {
      try {
        setSubmitting(true);
        setError(null);
        const result = await apiClient.post<ConfirmAirbnbResponse>('/upsells/confirm-airbnb', {
          upsellId,
          selection,
        });
        const params = new URLSearchParams();
        params.set('flow', result.flow);
        params.set('title', result.title);
        params.set('price', String(result.price));
        params.set('currency', result.currency);
        if (result.alterationNotice24h) params.set('notice', '24h');
        if (selection.type === 'option') params.set('optionName', selection.optionName);
        if (result.airbnbUrl) params.set('airbnbUrl', result.airbnbUrl);
        navigate(`${instructionsPath}?${params.toString()}`);
      } catch (err) {
        setError(
          chargeErrorCopy(
            (err as { code?: string } | undefined)?.code,
            err instanceof Error ? err.message : undefined
          )
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // BES-7449: gated hosts must e-sign a credit-card authorization before the
    // payment screen. Open the auth modal first, then proceed on completion.
    if (ccAuthRequired) {
      const upsell = upsells.find((u) => u.id === upsellId);
      const breakdown = upsell ? calculateTotal(upsell.upsellData, selection) : null;
      const amount = breakdown ? breakdown.total / 100 : 0;
      if (amount > 0) {
        setCcAuthPending({
          authType: 'charge',
          amount,
          currency: upsell?.upsellData.currency ?? 'USD',
          context: { kind: 'upsell', upsellId },
          run: () => proceedUpsellPurchase(upsellId, selection, appliedLoyaltyCredit),
        });
        return;
      }
    }

    await proceedUpsellPurchase(upsellId, selection, appliedLoyaltyCredit);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('Loading add-ons...')}</p>
        </div>
      </div>
    );
  }

  if (error && upsells.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] p-8">
        <div className="text-center space-y-6">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('Error Loading Add-Ons')}</h2>
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
          <Button onClick={() => window.location.reload()} className="w-full">
            {t('Retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (upsells.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] p-8">
        <div className="text-center space-y-5">
          <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('No Add-Ons Available')}</h2>
            <p className="text-gray-600 mt-1">{t('There are no add-ons available for your stay at this time.')}</p>
          </div>
        </div>
      </div>
    );
  }

  // BES-7462: re-open the Airbnb payment instructions the guest saw on confirm.
  const resumeAwaitingPayment = (upsell: UpsellWithPurchases) => {
    const ins = upsell.instructions;
    if (!ins) return;
    const params = new URLSearchParams();
    params.set('flow', ins.flow);
    params.set('title', upsell.upsellData.title);
    if (ins.priceMinor != null) params.set('price', String(ins.priceMinor));
    params.set('currency', ins.currency);
    if (ins.optionName) params.set('optionName', ins.optionName);
    if (ins.airbnbUrl) params.set('airbnbUrl', ins.airbnbUrl);
    navigate(`${instructionsPath}?${params.toString()}`);
  };

  // BES-7535/BES-7462: split add-ons into actionable pending-payment, interactive
  // available, and the grayed "no longer available" rows. An awaiting_payment row
  // without instructions (no parked event matched) degrades into the grayed bucket.
  const isPending = (u: UpsellWithPurchases) => u.unavailableReason === 'awaiting_payment' && !!u.instructions;
  const pendingUpsells = upsells.filter(isPending);
  const availableUpsells = upsells.filter((u) => !u.unavailableReason);
  const unavailableUpsells = upsells.filter((u) => u.unavailableReason && !isPending(u));

  return (
    <>
      {ccAuthPending && (
        <CcAuthModal
          authType={ccAuthPending.authType}
          amount={ccAuthPending.amount}
          currency={ccAuthPending.currency}
          context={ccAuthPending.context}
          onAuthorized={() => {
            const pending = ccAuthPending;
            setCcAuthPending(null);
            void pending.run();
          }}
          onCancel={() => setCcAuthPending(null)}
        />
      )}
      {cardModalState && (
        <UpsellGuestyPayCardModal
          paymentProviderId={cardModalState.paymentProviderId}
          listingId={cardModalState.listingId}
          pmsReservationId={cardModalState.pmsReservationId}
          upsellId={cardModalState.upsellId}
          selection={cardModalState.selection}
          amount={cardModalState.amount}
          currency={cardModalState.currency}
          appliedLoyaltyCredit={cardModalState.appliedLoyaltyCredit}
          onSuccess={() => {
            const upsell = upsells.find((u) => u.id === cardModalState.upsellId);
            const breakdown = upsell ? calculateTotal(upsell.upsellData, cardModalState.selection) : null;
            setCardModalState(null);
            void refetchPortal?.();
            navigate(
              `${resultPath}?status=success&upsellId=${cardModalState.upsellId}&amount=${breakdown?.total ?? 0}&currency=${upsell?.upsellData.currency ?? 'USD'}`
            );
          }}
          onCancel={() => setCardModalState(null)}
        />
      )}
      <div className="py-4">
        <LanguageSelector />
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mb-1">{t('Add-Ons')}</h1>
        <p className="text-[15px] text-zinc-500 mb-6">
          {availableUpsells.length > 0
            ? t('Enhance your stay with these available add-ons.')
            : t('Add-ons for your stay')}
        </p>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {pendingUpsells.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-bold tracking-tight text-zinc-900 mb-1">{t('Finish your payment')}</h2>
            <p className="text-sm text-zinc-500 mb-4">
              {t('You confirmed these add-ons. Complete payment on Airbnb to lock them in.')}
            </p>
            <div className="space-y-5">
              {pendingUpsells.map((upsell) => (
                <PendingPaymentCard key={upsell.id} upsell={upsell} onResume={() => resumeAwaitingPayment(upsell)} />
              ))}
            </div>
          </div>
        )}

        {availableUpsells.length > 0 && (
          <div className="space-y-5">
            {availableUpsells.map((upsell) => (
              <UpsellCard
                key={upsell.id}
                upsell={upsell}
                onPurchase={handlePurchase}
                submitting={submitting}
                isAirbnb={isAirbnb}
                offPlatformEnabled={offPlatformEnabled}
                loyaltyBalance={loyaltyBalance}
              />
            ))}
          </div>
        )}

        {unavailableUpsells.length > 0 && (
          <UnavailableSectionBoundary>
            <div className="mt-10">
              <h2 className="text-lg font-bold tracking-tight text-zinc-700 mb-1">{t('No longer available')}</h2>
              <p className="text-sm text-zinc-500 mb-4">
                {t('These add-ons are not available for your reservation right now.')}
              </p>
              <div className="space-y-5">
                {unavailableUpsells.map((upsell) => (
                  <UnavailableUpsellCard key={upsell.id} upsell={upsell} />
                ))}
              </div>
            </div>
          </UnavailableSectionBoundary>
        )}
      </div>
    </>
  );
}

export default Upsells;
